/**
 * Every string `/content` renders.
 *
 * **Catalog reasoning stays out of the interface.** The operator needs to know
 * *that* nothing stands between saving and the viewer seeing it; they have no
 * use for the sentence «a configuration key is false», and the review states
 * that key switches off draw no chip because nothing ever enters them.
 */
export const copy = {
  crumb: 'المحتوى',

  head: {
    title: 'المحتوى',
    lede: 'كلّ عناوين الفهرس في كلّ حالات النشر — المنشور والمسوّدة وغير المنشور معاً. النشر يقع من هذه القائمة، والتحرير والأصول من قائمة الصفّ.',
  },

  facets: {
    label: 'تصفية القائمة',
    state: 'حالة النشر',
    type: 'النوع',
    allStates: 'الكلّ',
    allTypes: 'النوعان',
    /** What the absence of a review step means for the operator's work. */
    noReviewNote: 'لا مراجعة بين الحفظ والنشر: ما تنشره يظهر للمشاهدين فوراً.',
  },

  section: {
    heading: 'العناوين',
    add: 'أضف عنواناً',
  },

  caption: 'عناوين الفهرس بحالات نشرها وأنواعها وتصنيفاتها وتواريخها',

  columns: {
    title: 'العنوان',
    type: 'النوع',
    state: 'الحالة',
    category: 'التصنيف',
    date: 'التاريخ',
    actions: 'أفعال العنوان',
  },

  /**
   * A published title whose media has not finished converting. It is not a
   * publish state — the record really is `منشور` — it is the reason nobody can
   * reach it, and the console is the only place that can say so.
   */
  notVisibleYet: 'غير مرئي للمستخدمين',
  notVisibleYetHint: 'ما زال التحويل جارياً؛ يظهر العنوان للمشتركين حال جاهزية أوّل ملفّ.',

  /** Absences the row states rather than leaving blank. */
  noCategory: 'بلا تصنيف',
  noDate: 'بلا تاريخ',

  /**
   * The film's shape line. It never counts episodes — a film's single asset is
   * not one, and borrowing the series' word for it would describe a shape the
   * record does not have.
   */
  shape: {
    filmHasFile: 'ملفٌّ واحد',
    filmHasNoFile: 'بلا ملفّ بعد',
  },

  rowMenu: {
    label: (title: string) => `أفعال عنوان «${title}»`,
    edit: 'حرّر العنوان',
    assetsSeries: 'الأصول والمواسم',
    assetsFilm: 'الأصول',
    publish: 'انشر الآن',
    republish: 'أعد النشر',
    unpublish: 'ألغِ النشر',
    /**
     * **The refusal, said before the press rather than after it.** `content`
     * gates publication on a rung of the ladder having finished, and the list
     * already knows the answer — `has_playable_asset` rides on every row. An
     * enabled entry that always fails is a control that lies; the entry stays
     * visible and disabled, and this line under the menu says why, because a
     * greyed entry with no reason reads as a fault.
     */
    publishNotReadyHint: 'النشر متاح بعد جهوز أوّل ملفّ للتشغيل. راجع صفحة الأصول لحالة التحويل.',
  },

  unpublish: {
    heading: (title: string) => `إلغاء نشر «${title}»؟`,
    body: 'تختفي صفحة العنوان من الفهرس العامّ فوراً، ويتوقّف تشغيله للمشتركين. الأصول والمواسم والتعليقات تبقى كما هي، والنشر يعيدها جميعاً.',
    commit: 'ألغِ النشر',
  },

  schedule: {
    heading: 'جدوِل النشر',
    headingEdit: 'عدّل موعد النشر',
    lede: 'تظهر صفحة العنوان في القوائم العامّة من الآن ليجدها الباحثون، ويبقى المشغّل مقفلاً حتّى هذا التاريخ.',
    dateLabel: 'تاريخ الإتاحة',
    dateHint: 'اليوم الذي تصير فيه صفحة العنوان مرئيّة. يُعرَض بالتقويم الميلاديّ.',
    /** The read-back line: what will happen, and when, before the press. */
    preview: (title: string, date: string) => `يُنشر «${title}» في ${date}.`,
    commit: 'جدوِل النشر',
    required: 'اختر تاريخ الإتاحة.',
    notInPast: 'اختر تاريخاً من اليوم فصاعداً.',
  },

  truncationTail: 'الأحدث أولاً.',

  /**
   * The traversal control. Every facet on this surface is a wire parameter, so a
   * page here is a page of the **filtered** list and both figures describe the
   * same set the table shows.
   */
  pager: {
    label: 'صفحات العناوين',
    shownOne: 'عنوان واحد معروض',
    shownTwo: 'عنوانان معروضان',
    shownFew: 'عناوين معروضة',
    shownMany: 'عنواناً معروضاً',
  },

  /** This list's own search — the console's only route to a draft by name. */

  search: {
    label: 'ابحث في العناوين',

    placeholder: 'ابحث بالعنوان…',

    hint: 'البحث يجري على كامل الفهرس بكل حالاته، لا على الصفحة المعروضة وحدها.',
  },

  states: {
    loading: 'جارٍ تحميل العناوين',
    empty: 'لا عنوان في الفهرس بعد. ابدأ بإضافة واحد.',
    emptyFiltered: 'لا عنوان يطابق هذه التصفية.',
    clearFilters: 'أظهر الكلّ',
    /**
     * A page walked past the end — not an empty catalogue and not a facet that
     * matched nothing. Each of the three has its own way out, so none may borrow
     * another's sentence.
     */
    emptyPage: 'لا عنوان في هذه الصفحة. عد إلى السابقة.',
  },

  toast: {
    published: (title: string) => `نُشر «${title}».`,
    unpublished: (title: string) => `أُلغي نشر «${title}».`,
    /** The publish refusal, with the outstanding fields named after it. */
    missingMetadata: 'لا يمكن النشر قبل استكمال الحقول المطلوبة:',
  },
} as const;
